import { useCallback } from 'react';
import type { Account, Address, Chain, Transport, WalletClient } from 'viem';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { getConnectedChain, getPrivyWalletClient } from '@/utils/wallet';

/**
 * The EVM wallet that owns the Hyperliquid account. Hyperliquid accepts
 * user-signed actions from any EVM chain id, so signing never requires a
 * chain switch; the wallet client is built on whatever chain the wallet is on.
 */
export function usePerpsWallet() {
  const { ethereumWallet, walletsReady, connect } = useMatchaWallets();
  const address = ethereumWallet?.address as Address | undefined;

  const getWalletClient = useCallback(async (): Promise<
    WalletClient<Transport, Chain, Account>
  > => {
    if (!ethereumWallet) throw new Error('Connect an Ethereum wallet to trade perps.');
    const chain = getConnectedChain(ethereumWallet);
    if (!chain) throw new Error('Switch your wallet to a network supported by Matcha.');
    const client = await getPrivyWalletClient(ethereumWallet, chain);
    if (!client) throw new Error('Could not connect to your wallet.');
    // getPrivyWalletClient always hoists the wallet address as the account.
    return client as WalletClient<Transport, Chain, Account>;
  }, [ethereumWallet]);

  return { address, ethereumWallet, walletsReady, connect, getWalletClient };
}
